import { StrictMode } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';

import { getUser } from '@simplywallst/services';
import { theme, ScreenSize } from '@simplywallst/ui-core';

import Analytics from '@simplywallst/analytics';
import { getViewMode } from '@/redux/location/selectors';

import { migrateStorage } from '@/utilities/capacitor';
import { googleTranslateHotfix } from '@/utilities/googleTranslateHotfix';
import { initialiseSentry, setUser as setSentryUser } from '@/utilities/sentry';
import {
  setAuthCookie,
  getAuthCookie,
  canReadWriteCookies,
  isAccessTokenExpiring,
  AuthTokens,
  doRefreshAccessToken,
} from '@utilities/cookies';

import { ViewMode } from '@/pages/CompanyReport/enum';
import { getInitialState } from '@/redux/auth/reducers';

import { SWS_LEGACY_HOST as IFRAME_DOMAIN } from '@/constants';

import { QUERY_KEY as USER_QUERY_KEY, reducer } from '@/hooks/useUser';

import configureStore from './App/store/configureStore';
import App from './App';

type UserResponse = Awaited<ReturnType<typeof getUser>>['data'];

googleTranslateHotfix();

const fetchUserWithToken = async ({
  accessToken,
}: {
  accessToken?: string;
}): Promise<UserResponse> => {
  const response = await getUser({
    Authorization: `Bearer ${accessToken}`,
  });
  return response.data;
};

const getInfo = async () => {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return info;
  } catch (err) {
    console.log('Failed to get device info', err);
  }
};

const setStatusBar = async () => {
  try {
    if (RUNTIME_ENV === 'native') {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({
        style: Style.Dark,
      });
    }
  } catch (err) {
    console.warn(err);
  }
};

async function renderApp(
  Component: typeof App,
  authCookie?: NonNullable<AuthTokens>,
  userResponse?: UserResponse
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typedWindow = window as any;
  if (RUNTIME_ENV === 'native') {
    try {
      // Note: removed await here to test if this is causing slow down during app startup
      setStatusBar();
    } catch (error) {
      console.error('Failed to set status bar');
    }
  }

  let platform = 'web';
  let appVersion = '';

  try {
    if (RUNTIME_ENV === 'native') {
      /**
       * This is redundant now since <AppVersion> calls this same function
       * and stops using redux as the source of appVersion.
       */
      const info = await getInfo();
      platform = 'mobile';
      appVersion = info?.version || '';
    }
  } catch (error) {
    console.error('Failed to get native info');
  }

  // Initiate Analytics
  typedWindow.analytics = new Analytics(platform);

  const dehydratedState = typedWindow.__REACT_QUERY_STATE__;
  const queryClient = new QueryClient();
  if (typeof userResponse?.data !== 'undefined') {
    queryClient.setQueryData(
      USER_QUERY_KEY,
      reducer(undefined, userResponse.data)
    );
  }
  //
  const preloadedState = typedWindow.REDUX_STATE;
  const preloadedToken = Object.assign({}, getInitialState(), authCookie);
  const { store, sagas, thunk } = configureStore(
    null,
    Object.assign({}, preloadedState, { tokens: preloadedToken }),
    false,
    null,
    {
      appVersion,
    },
    queryClient
  );

  const state = store.getState();
  const viewMode = getViewMode(state);
  const themeSettings = {
    screenSize:
      viewMode === ViewMode.Screen ? ScreenSize.Default : ScreenSize.Print,
  };
  const appTheme = theme(themeSettings);

  /**
   * Run sagas and run routes thunk
   */
  const sagaTask = sagas.runSaga(sagas.rootSagas);

  /** This prevents route actions from refiring if SSR is available */
  if (typeof typedWindow.REDUX_STATE === 'undefined') {
    try {
      await thunk(store);
    } catch (error) {
      console.error(error);
    }
  }

  //Set Document domain for IFrames
  try {
    const host = typedWindow.location.hostname;
    if (host && host.indexOf(IFRAME_DOMAIN) > -1) {
      console.log(`Setting domain to ${IFRAME_DOMAIN}`);
      document.domain = IFRAME_DOMAIN;
    } else {
      console.log('Skipping domain setting for loop-back address');
    }
  } catch (err) {
    console.error('Failed to set domain..', err);
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('No root element found');
  const root = createRoot(rootEl);
  root.render(
    <StrictMode>
      <Component
        theme={appTheme}
        store={store}
        serverState={preloadedState}
        queryClient={queryClient}
        dehydratedState={dehydratedState}
        locationOrigin={window.location.origin}
        setReduxSagaContext={sagaTask.setContext}
      />
    </StrictMode>
  );
}

async function startApp() {
  try {
    initialiseSentry();
  } catch (e) {
    console.error(e);
  }

  try {
    if (RUNTIME_ENV === 'native') {
      migrateStorage();
    }
  } catch (e) {
    console.error(e);
  }

  let authCookie: AuthTokens | undefined;
  let userResponse: UserResponse | undefined;
  try {
    if (canReadWriteCookies()) {
      try {
        authCookie = await getAuthCookie();
      } catch (error) {
        console.error('Failed to get cookies');
      }
      if (typeof authCookie !== 'undefined') {
        let accessToken: string | undefined = authCookie.accessToken;
        if (isAccessTokenExpiring(authCookie.expiresAt)) {
          try {
            authCookie = await doRefreshAccessToken(authCookie);
            accessToken = authCookie.accessToken;
          } catch (error) {
            console.error('Failed to refresh token');
            accessToken = undefined;
            setAuthCookie('', '');
          }
        }
        if (typeof accessToken !== 'undefined') {
          /** attempt to fetch api/user */
          userResponse = await fetchUserWithToken({ accessToken });
          const { logged_in } = userResponse.data;

          if (!logged_in) {
            try {
              authCookie = await doRefreshAccessToken(authCookie);
              accessToken = authCookie.accessToken;
              userResponse = await fetchUserWithToken({ accessToken });
              const { logged_in } = userResponse.data;
              if (!logged_in) {
                throw new Error('Failed to authenticate');
              }
            } catch (error) {
              console.error('Failed to refresh token');
              setAuthCookie('', '');
              userResponse = undefined;
              authCookie = undefined;
            }
          }
        }
      } else {
        // Fetch unauthenticated user for default properties. Example: currencyISO, used for default market
        const { data } = await getUser();
        userResponse = data;
      }
    }
  } catch (error) {
    userResponse = undefined;
    console.error('Failed to read write cookies');
  }

  if (userResponse?.data?.logged_in) {
    setSentryUser(userResponse.data);
  }

  if (
    NODE_ENV === 'development' &&
    typeof (module as any).hot !== 'undefined'
  ) {
    (module as any).hot.accept();
    renderApp(App, authCookie, userResponse);
  } else {
    loadableReady(() => renderApp(App, authCookie, userResponse));
  }
}

startApp();
